import { useObjectUrl, useObjectUrls } from '../../hooks/useObjectUrl';
import type { ImageDimensions } from '../../utils/imageDimensions';
import { getMediaType } from '../../utils/mediaUtils';
import { DappPreviewShell } from '../shared/DappPreviewShell';

interface FormData {
  dappName: string;
  subtitle: string;
  description: string;
  dappIcon: File | null;
  banner: File | null;
  dappPreviews: (File | null)[];
}

interface DappPreviewPanelProps {
  formData: FormData;
  previewDimensions?: ImageDimensions | null;
}

export function DappPreviewPanel({
  formData,
  previewDimensions,
}: DappPreviewPanelProps) {
  const dappPreviewUrls = useObjectUrls(formData.dappPreviews);
  const iconPreviewUrl = useObjectUrl(formData.dappIcon);
  const bannerPreviewUrl = useObjectUrl(formData.banner);

  const previewSlots = dappPreviewUrls.map((previewUrl, index) => {
    const filePreview = formData.dappPreviews[index];
    const slotKey = `preview-slot-${index}`;

    if (!previewUrl) {
      return null;
    }

    const mediaType = getMediaType(filePreview || previewUrl);

    if (mediaType === 'video') {
      return (
        <video
          key={slotKey}
          src={previewUrl}
          aria-label={`Preview ${index + 1} video`}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-contain rounded-2xl"
        />
      );
    }

    return (
      <img
        key={slotKey}
        src={previewUrl}
        alt={`Preview ${index + 1}`}
        className="w-full h-full object-cover rounded-2xl"
      />
    );
  });

  return (
    <DappPreviewShell
      dappName={formData.dappName}
      subtitle={formData.subtitle}
      description={formData.description}
      bannerSlot={
        bannerPreviewUrl ? (
          <img
            src={bannerPreviewUrl}
            alt="Banner Preview"
            className="w-full h-full object-cover"
          />
        ) : null
      }
      iconSlot={
        iconPreviewUrl ? (
          <img
            src={iconPreviewUrl}
            alt="Icon Preview"
            className="w-full h-full object-cover rounded-3xl"
          />
        ) : null
      }
      previewSlots={previewSlots}
      previewDimensions={previewDimensions}
    />
  );
}
